/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Button, Stack, useTheme } from "@mui/material";
import { cloneDeep, difference, union } from "lodash";
import React, { useMemo } from "react";
import { useSelector } from "react-redux";

import ContactCard from "../../../../components/ContactCard";
import {
  EditPriceModal,
  IEditedCartItemDetails,
} from "../../../../components/modals/EditPriceModal/EditPriceModal";
import { ItemLabel } from "../../../../components/styled";
import { usePermission } from "../../../../hooks/usePermission";
import { selectRemoteConfig } from "../../../../services/firebase/remote-config/remoteConfig.slice";
import TestIds from "../../../../testIds";
import { addGST } from "../../../../util/addGst";
import { selectCurrentFarmerAccount } from "../../../azure-b2c/storage";
import { CartItem } from "../../../cart/storage/cart.types";
import { SideContainer } from "../../../products/components/ProductCard";
import { CartItemCard } from "../../../products/components/ProductCard/CartItemCard";
import { CounterView } from "../../../products/components/ProductCard/ProductCardParent";
import { Quote, QuoteStatus } from "../../storage/types";
import {
  calculateNewBasePriceExcGst,
  calculateNewDiscountedPriceExcGst,
  calculatePriceForNewQuantity,
} from "../../utils/calculateTotal";
import { showQuoteAsTwoColumnLayout } from "../../utils/getQuoteStatus";

import QuoteAddProductModal from "./QuoteAddProductModal/QuoteAddProductModal";
import { QuoteDetailInfo } from "./QuoteDetailInfo";
import { QuoteProductCard } from "./QuoteProductCard";

export interface QuoteDetailsProductsProps {
  quote: Quote;
  originalProducts: CartItem[];
  products: CartItem[];
  setProducts: (products: CartItem[]) => void;
}

export const QuoteDetailsProducts = ({
  quote,
  originalProducts,
  products,
  setProducts,
}: QuoteDetailsProductsProps) => {
  const theme = useTheme();
  const account = useSelector(selectCurrentFarmerAccount);
  const remoteConfig = useSelector(selectRemoteConfig);
  const canEditPrice = usePermission("StaffSpecific_Pricing_PriceEdit").allowed;

  const [openEditPriceModal, setOpenEditPriceModal] = React.useState(false);
  const [openAddProductModal, setOpenAddProductModal] = React.useState(false);
  const [editPriceItem, setEditPriceItem] = React.useState<CartItem>();

  const checkProductQuantity = (productId: number) => {
    const originalProduct = originalProducts.find((p) => p.productId === productId);
    const product = products.find((p) => p.productId === productId);

    if (originalProduct && product && quote.isLocked) {
      if (originalProduct.quantity > product.quantity) {
        changeProductQuantity(originalProduct.quantity, productId);
      }
    }
  };

  const changeProductQuantity = (value: number, productId: number, blur?: boolean) => {
    const product = products.find((p) => p.productId === productId);

    if (product) {
      if (
        blur &&
        (!account?.hasTfoAccess || (account.hasTfoAccess && quote.status === QuoteStatus.Offered))
      ) {
        checkProductQuantity(productId);
        return;
      }
      const discountedPriceExcGst = calculateNewDiscountedPriceExcGst(product, value);
      product.discountedPriceExcGst = discountedPriceExcGst;
      product.discountedPriceIncGst = addGST(discountedPriceExcGst);
      product.basePriceExcGst = calculateNewBasePriceExcGst(product, value);
      product.quantity = value;
      setProducts(cloneDeep(products));
    }
  };

  const removeItem = (item: CartItem) => {
    setProducts(cloneDeep(products.filter((p) => p.productId !== item.productId)));
  };

  const restoreItem = (item: CartItem) => {
    setProducts(cloneDeep([...products, item]));
  };

  const renderProduct = (item: CartItem, index: number, status: QuoteStatus) => {
    switch (true) {
      case QuoteStatus.Offered === status: {
        const originalProduct = originalProducts.find((p) => p.productId === item.productId);
        const hideDownCount =
          quote.isLocked &&
          ((originalProduct && originalProduct?.quantity >= item.quantity) || false);
        const handleOpenEditPriceModal = account?.hasTfoAccess
          ? () => {
              setEditPriceItem({ ...item, originalPriceExcGst: item.basePriceExcGst || 0 });
              setOpenEditPriceModal(true);
            }
          : undefined;

        return (
          <QuoteProductCard
            key={`{product.productId}_${index}`}
            item={{ ...item }}
            enableRemove={!quote?.isLocked}
            hideDownCount={hideDownCount}
            onChangeQuantity={(value, blur) => {
              changeProductQuantity(value, item.productId, blur);
            }}
            priceEditable={
              (remoteConfig.discountEnabled && canEditPrice && account?.hasTfoAccess) || false
            }
            handleOpenEditPriceModal={handleOpenEditPriceModal}
            onRemove={
              products.length <= 1
                ? undefined
                : () => {
                    removeItem(item);
                  }
            }
          />
        );
      }

      case QuoteStatus.Requested === status && account?.hasTfoAccess: {
        const handleOpenEditPriceModal = account?.hasTfoAccess
          ? () => {
              setEditPriceItem({ ...item, originalPriceExcGst: item.basePriceExcGst || 0 });
              setOpenEditPriceModal(true);
            }
          : undefined;
        return (
          <QuoteProductCard
            key={`{product.productId}_${index}`}
            item={{ ...item }}
            enableRemove={!quote?.isLocked}
            hideDownCount={false}
            onChangeQuantity={(value, blur) => {
              changeProductQuantity(value, item.productId, blur);
            }}
            priceEditable={(remoteConfig.discountEnabled && account?.hasTfoAccess) || false}
            enableQuantity={account?.hasTfoAccess || false}
            handleOpenEditPriceModal={handleOpenEditPriceModal}
            onRemove={
              products.length <= 1
                ? undefined
                : () => {
                    removeItem(item);
                  }
            }
          />
        );
      }

      default: {
        return (
          <CartItemCard
            key={`{product.productId}_${index}`}
            item={item}
            onRemove={undefined}
            onChangeVariant={undefined}
            enableVariants={true}
            handleOpenEditPriceModal={undefined}
            priceEditable={false}
            renderSideContainer={
              <SideContainer>
                <CounterView>
                  <ItemLabel variant="body2">{`Quantity ${item.quantity}`}</ItemLabel>
                </CounterView>
              </SideContainer>
            }
            showUrgentDeliveryUnavailableMessage={false}
          />
        );
      }
    }
  };

  const renderMissingProduct = (productId: number, index: number) => {
    const product = originalProducts.find((p) => p.productId === productId);

    if (product) {
      return (
        <CartItemCard
          key={`original_{product.productId}_${index}`}
          sx={{ backgroundColor: theme.colors.grey100 }}
          item={product}
          onRemove={undefined}
          onChangeVariant={undefined}
          enableVariants={true}
          handleOpenEditPriceModal={undefined}
          priceEditable={false}
          renderSideContainer={
            <SideContainer>
              <Button
                data-testid={TestIds.feat.product.card.restoreButton}
                variant="outlined"
                onClick={() => restoreItem(product)}
              >
                Restore
              </Button>
            </SideContainer>
          }
          showUrgentDeliveryUnavailableMessage={false}
        />
      );
    }
  };

  const renderRemovedProducts = useMemo(() => {
    const productIds = products.map((p) => p.productId);
    const originalProductIds = originalProducts.map((p) => p.productId);
    if (union(productIds, originalProductIds).length === productIds.length) {
      return null;
    }

    const missingProductIds = difference(originalProductIds, productIds);
    return missingProductIds.map((id, index) => renderMissingProduct(id, index));
  }, [products, originalProducts]);

  const renderProducts = quote
    ? products.map((product, index) => renderProduct(product, index, quote.status))
    : undefined;

  const onRevertEditedPrice = async ({ productId }: CartItem) => {
    const originalProduct = originalProducts.find((p) => p.productId === productId);
    const product = products.find((p) => p.productId === productId);

    if (originalProduct && product) {
      const discountedPriceExcGst = calculatePriceForNewQuantity(
        originalProduct.discountedPriceExcGst,
        originalProduct.quantity,
        product.quantity
      );
      product.discountedPriceExcGst = discountedPriceExcGst;
      product.discountedPriceIncGst = addGST(discountedPriceExcGst);
      product.basePriceExcGst = calculatePriceForNewQuantity(
        originalProduct.basePriceExcGst,
        originalProduct.quantity,
        product.quantity
      );
      product.editedPriceExcGst = originalProduct.editedPriceExcGst;
      product.lastEditedReasonCode = originalProduct.lastEditedReasonCode;
      product.lastEditedReasonNote = originalProduct.lastEditedReasonNote;
      setProducts(cloneDeep(products));
    }
  };

  const renderAddProductButton = useMemo(() => {
    if (account?.hasTfoAccess && quote.status === QuoteStatus.Requested) {
      return (
        <Stack direction="row">
          <Button variant="outlined" onClick={() => setOpenAddProductModal(true)}>
            Add Product
          </Button>
        </Stack>
      );
    }
    return null;
  }, [account?.hasTfoAccess, quote.status]);

  const onApplyEditedPrice = ({
    editedPrice,
    editedPriceReasonCode,
    editedPriceReasonNote,
    editedQuantity,
    item,
  }: IEditedCartItemDetails) => {
    const product = products.find((p) => p.productId === item.productId);

    if (product) {
      product.editedPriceExcGst = editedPrice;
      product.lastEditedReasonCode = editedPriceReasonCode;
      product.lastEditedReasonNote = editedPriceReasonNote;
      product.quantity = editedQuantity;
      const discountedPriceExcGst = editedPrice * editedQuantity;
      product.discountedPriceExcGst = discountedPriceExcGst;
      product.discountedPriceIncGst = addGST(discountedPriceExcGst);
      setProducts(cloneDeep(products));
    }
  };

  const handleOnAddProduct = (product: CartItem) => {
    const newProducts = cloneDeep(products);
    const productFound = newProducts.find((p) => p.productId === product.productId);

    if (productFound) {
      setProducts(
        newProducts.map((p) => {
          const productIsAdded = p.productId === productFound.productId;
          if (productIsAdded) {
            return {
              ...p,
              quantity: productFound.quantity + 1,
            };
          }
          return p;
        })
      );
    } else {
      newProducts.push(product);
      setProducts(newProducts);
    }
  };

  return (
    <Stack
      direction="column"
      flexGrow={1}
      gap={2}
      width={!showQuoteAsTwoColumnLayout(quote) ? "100%" : "unset"}
    >
      {renderProducts}
      {renderAddProductButton}
      {renderRemovedProducts}

      <QuoteDetailInfo quote={quote} products={products} />

      {editPriceItem && (
        <EditPriceModal
          item={editPriceItem}
          open={openEditPriceModal}
          setOpen={setOpenEditPriceModal}
          onApplyEditedPrice={onApplyEditedPrice}
          onRevertEditedPrice={onRevertEditedPrice}
          cartProductCardProps={{
            enableRemove: false,
          }}
        />
      )}

      <QuoteAddProductModal
        open={openAddProductModal}
        setOpen={setOpenAddProductModal}
        onAddProduct={handleOnAddProduct}
      />

      {quote.offeredBy ? (
        <ContactCard
          title="The quote was created by:"
          name={quote.offeredBy.offeredByName}
          email={quote.offeredBy.offeredByEmail}
          sx={{ maxWidth: "unset" }}
        />
      ) : null}
    </Stack>
  );
};
